import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase.config';

interface Application {
  id: string;
  companyName: string;
  position: string;
  appliedDate: any;
  status: string;
}

interface UseUserApplicationsReturn {
  companies: string[];
  loading: boolean;
  error: string | null;
  loadUserApplications: (userId: string) => Promise<void>;
}

export const useUserApplications = (): UseUserApplicationsReturn => {
  const [companies, setCompanies] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadUserApplications = async (userId: string) => {
    if (!userId) {
      setCompanies([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const applications: Application[] = [];
      const companySet = new Set<string>();

      // Prima prova la collection principale
      try {
        const applicationsRef = collection(db, 'applications');
        const q = query(
          applicationsRef,
          where('userId', '==', userId)
        );

        const querySnapshot = await getDocs(q);
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          applications.push({
            id: doc.id,
            companyName: data.companyName || '',
            position: data.position || '',
            appliedDate: data.appliedDate,
            status: data.status || ''
          });
          
          if (data.companyName) {
            companySet.add(data.companyName);
          }
        });
      } catch (rootError) {
        console.log('Nessuna candidatura nella collection principale per utente:', userId);
      }

      // Se non trova nulla nella collection principale, prova nelle subcollections
      if (applications.length === 0) {
        try {
          const userApplicationsRef = collection(db, 'users', userId, 'applications');
          const userApplicationsSnapshot = await getDocs(userApplicationsRef);
          
          userApplicationsSnapshot.forEach((doc) => {
            const data = doc.data();
            applications.push({
              id: doc.id,
              companyName: data.companyName || '',
              position: data.position || '',
              appliedDate: data.appliedDate,
              status: data.status || ''
            });
            
            if (data.companyName) {
              companySet.add(data.companyName);
            }
          });
        } catch (subError) {
          console.log('Nessuna candidatura nelle subcollections per utente:', userId);
        }
      }

      const uniqueCompanies = Array.from(companySet).sort();
      
      setCompanies(uniqueCompanies);
    } catch (error) {
      console.error('Error loading user applications:', error);
      setError('Errore nel caricamento delle candidature');
    } finally {
      setLoading(false);
    }
  };

  return {
    companies,
    loading,
    error,
    loadUserApplications
  };
}; 